import type {
  ExperienceSettings,
  QualityLevel,
  TimePreset,
  WeatherKind,
} from "../core/types";
import { DEFAULT_SETTINGS } from "../core/types";

const WEATHER_VALUES: readonly WeatherKind[] = [
  "clear",
  "cloudy",
  "overcast",
  "mist",
  "drizzle",
  "after-rain",
];
const TIME_VALUES: readonly TimePreset[] = [
  "dawn",
  "morning",
  "noon",
  "golden-hour",
  "night",
];
const QUALITY_VALUES: readonly QualityLevel[] = ["medium", "high"];

export function cloneSettings(settings: ExperienceSettings): ExperienceSettings {
  return { ...settings, comfort: { ...settings.comfort } };
}

export function normalizeSettings(
  input:
    | (Partial<Omit<ExperienceSettings, "comfort">> & {
        comfort?: Partial<ExperienceSettings["comfort"]>;
      })
    | null
    | undefined,
  fallback: ExperienceSettings = DEFAULT_SETTINGS,
): ExperienceSettings {
  const comfort = input?.comfort;
  return {
    weather: isOneOf(input?.weather, WEATHER_VALUES)
      ? input.weather
      : fallback.weather,
    timePreset: isOneOf(input?.timePreset, TIME_VALUES)
      ? input.timePreset
      : fallback.timePreset,
    timeFlow: asBoolean(input?.timeFlow, fallback.timeFlow),
    residents: asBoolean(input?.residents, fallback.residents),
    animals: asBoolean(input?.animals, fallback.animals),
    quality: isOneOf(input?.quality, QUALITY_VALUES)
      ? input.quality
      : fallback.quality,
    masterVolume: clampFinite(input?.masterVolume, 0, 1, fallback.masterVolume),
    comfort: {
      fov: clampFinite(comfort?.fov, 55, 85, fallback.comfort.fov),
      headBob: asBoolean(comfort?.headBob, fallback.comfort.headBob),
      motionBlur: asBoolean(
        comfort?.motionBlur,
        fallback.comfort.motionBlur,
      ),
      interactionHints: asBoolean(
        comfort?.interactionHints,
        fallback.comfort.interactionHints,
      ),
    },
  };
}

export function sanitizeSeed(value: string): string {
  return value.trim().replace(/\s+/g, "-").slice(0, 64);
}

export function seedFromLocation(location: Pick<Location, "search">): string | null {
  const seed = sanitizeSeed(new URLSearchParams(location.search).get("seed") ?? "");
  return seed || null;
}

export function createSeed(random: () => number = Math.random): string {
  const value = Math.floor(random() * 0xffff_ffff)
    .toString(36)
    .padStart(6, "0");
  return `jiangnan-${value}`;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function clampFinite(
  value: unknown,
  min: number,
  max: number,
  fallback: number,
): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.min(max, Math.max(min, value))
    : fallback;
}

function isOneOf<T>(value: unknown, values: readonly T[]): value is T {
  return values.includes(value as T);
}
